import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { LanguageProvider } from "./contexts/LanguageContext";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { CartProvider } from "./contexts/CartContext";
import Layout from "./components/Layout";

// Public Pages
import HomePage from "./pages/HomePage";
import AboutPage from "./pages/AboutPage";
import ServicesPage from "./pages/ServicesPage";
import ProjectsPage from "./pages/ProjectsPage";
import TrainingPage from "./pages/TrainingPage";
import CareersPage from "./pages/CareersPage";
import ShopPage from "./pages/ShopPage";
import CartPage from "./pages/CartPage";
import ContactPage from "./pages/ContactPage";
import PartnersPage from "./pages/PartnersPage";
import DailyLifePage from "./pages/DailyLifePage";
import LoginPage from "./pages/LoginPage";

// Admin Pages
import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminServices from "./pages/admin/AdminServices";
import AdminProjects from "./pages/admin/AdminProjects";
import AdminProducts from "./pages/admin/AdminProducts";
import AdminTrainings from "./pages/admin/AdminTrainings";
import AdminJobs from "./pages/admin/AdminJobs";
import AdminPartners from "./pages/admin/AdminPartners";
import AdminDailyLife from "./pages/admin/AdminDailyLife";
import AdminGallery from "./pages/admin/AdminGallery";
import AdminContacts from "./pages/admin/AdminContacts";
import AdminJobApplications from "./pages/admin/AdminJobApplications";
import AdminTrainingRegistrations from "./pages/admin/AdminTrainingRegistrations";
import AdminPartnershipRequests from "./pages/admin/AdminPartnershipRequests";
import AdminQuoteRequests from "./pages/admin/AdminQuoteRequests";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminBackup from "./pages/admin/AdminBackup";

// Protected Route Component
function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0B6AB3]"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
}

// Public Layout Wrapper
function PublicLayout({ children }) {
  return <Layout>{children}</Layout>;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<PublicLayout><HomePage /></PublicLayout>} />
      <Route path="/about" element={<PublicLayout><AboutPage /></PublicLayout>} />
      <Route path="/services" element={<PublicLayout><ServicesPage /></PublicLayout>} />
      <Route path="/projects" element={<PublicLayout><ProjectsPage /></PublicLayout>} />
      <Route path="/training" element={<PublicLayout><TrainingPage /></PublicLayout>} />
      <Route path="/careers" element={<PublicLayout><CareersPage /></PublicLayout>} />
      <Route path="/shop" element={<PublicLayout><ShopPage /></PublicLayout>} />
      <Route path="/cart" element={<PublicLayout><CartPage /></PublicLayout>} />
      <Route path="/contact" element={<PublicLayout><ContactPage /></PublicLayout>} />
      <Route path="/partners" element={<PublicLayout><PartnersPage /></PublicLayout>} />
      <Route path="/daily-life" element={<PublicLayout><DailyLifePage /></PublicLayout>} />
      <Route path="/login" element={<PublicLayout><LoginPage /></PublicLayout>} />
      
      {/* Admin Routes */}
      <Route path="/admin" element={
        <ProtectedRoute>
          <AdminLayout />
        </ProtectedRoute>
      }>
        <Route index element={<AdminDashboard />} />
        <Route path="services" element={<AdminServices />} />
        <Route path="projects" element={<AdminProjects />} />
        <Route path="products" element={<AdminProducts />} />
        <Route path="trainings" element={<AdminTrainings />} />
        <Route path="jobs" element={<AdminJobs />} />
        <Route path="partners" element={<AdminPartners />} />
        <Route path="daily-life" element={<AdminDailyLife />} />
        <Route path="gallery" element={<AdminGallery />} />
        <Route path="contacts" element={<AdminContacts />} />
        <Route path="job-applications" element={<AdminJobApplications />} />
        <Route path="training-registrations" element={<AdminTrainingRegistrations />} />
        <Route path="partnership-requests" element={<AdminPartnershipRequests />} />
        <Route path="quote-requests" element={<AdminQuoteRequests />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="backup" element={<AdminBackup />} />
      </Route>

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <AuthProvider>
          <CartProvider>
            <AppRoutes />
          </CartProvider>
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
